import React, { FC, useEffect, useRef } from 'react';
import ReactConfetti from 'react-confetti';
import styles from './styles.module.css';

export type ScratchResultModalProps = {
  isWin: boolean;
  prizeAmount: number | null;
  /** Prize tier label e.g. "Small", "Medium", "Large", "Super". */
  tierName?: string;
  /** Full win body copy — passed from the caller. Only rendered on win. */
  outcomeDescription?: string;
  remainingCards: number;
  hasRemainingCards: boolean;
  onScratchAnother: () => void;
  onClose: () => void;
};

// Ticket badge SVG — used in the win heading.
const TicketBadge: FC = () => (
  <svg
    className={styles.ticketBadge}
    viewBox="0 0 48 48"
    aria-hidden="true"
    width="48"
    height="48"
  >
    <rect width="48" height="48" rx="10" fill="#d75d63" />
    <rect
      x="6"
      y="16"
      width="36"
      height="16"
      rx="3"
      fill="rgba(255,255,255,0.9)"
    />
    <circle cx="16" cy="24" r="3" fill="#d75d63" />
    <line
      x1="22"
      y1="18"
      x2="22"
      y2="30"
      stroke="rgba(215,93,99,0.4)"
      strokeWidth="1"
      strokeDasharray="2,2"
    />
    <rect
      x="24"
      y="20"
      width="14"
      height="2"
      rx="1"
      fill="rgba(215,93,99,0.5)"
    />
    <rect
      x="24"
      y="23.5"
      width="10"
      height="1.5"
      rx="0.75"
      fill="rgba(215,93,99,0.4)"
    />
    <rect
      x="24"
      y="26.5"
      width="12"
      height="1.5"
      rx="0.75"
      fill="rgba(215,93,99,0.4)"
    />
  </svg>
);

export const ScratchResultModal: FC<ScratchResultModalProps> = ({
  isWin,
  prizeAmount,
  tierName,
  outcomeDescription,
  remainingCards,
  hasRemainingCards,
  onScratchAnother,
  onClose,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const showWinPrize = isWin && prizeAmount != null;

  // Focus the heading on mount so screen readers announce the dialog.
  useEffect(() => {
    headingRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Trap Tab/Shift-Tab within the modal; Escape dismisses.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusable = Array.from(
      modal.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    // idx is -1 when activeElement is outside the focusable set (e.g. the heading
    // on mount, which has tabIndex={-1} and is intentionally excluded).
    const idx = focusable.indexOf(document.activeElement as HTMLElement);

    if (e.shiftKey) {
      if (idx <= 0) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (idx === -1 || idx === focusable.length - 1) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div
      className={styles.overlay}
      data-testid="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scratch-result-heading"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {showWinPrize && (
        <ReactConfetti
          numberOfPieces={150}
          recycle={false}
          gravity={0.2}
          colors={['#f6c04b', '#edac39', '#d75d63', '#9eebfd', '#ffffff']}
        />
      )}

      <div className={styles.card} ref={modalRef}>
        {showWinPrize && (
          <div className={styles.badgeRow}>
            <TicketBadge />
          </div>
        )}

        <p className={styles.eyebrow}>
          {showWinPrize ? 'THREE MATCH — YOU WIN' : 'SORRY TRY AGAIN'}
        </p>

        <h2
          className={showWinPrize ? styles.headingWin : styles.headingNonWin}
          id="scratch-result-heading"
          data-testid="outcome-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {showWinPrize
            ? `$${prizeAmount.toLocaleString('en-US')}`
            : 'Sorry, try again'}
        </h2>

        {showWinPrize && tierName && (
          <p className={styles.tierLabel} data-testid="prize-tier-label">
            {tierName}
          </p>
        )}

        {showWinPrize && outcomeDescription && (
          <p className={styles.description}>{outcomeDescription}</p>
        )}

        <div className={styles.remainingRow}>
          <span className={styles.remainingLabel}>Cards remaining</span>
          <span className={styles.remainingBadge}>{remainingCards}</span>
        </div>

        {hasRemainingCards && (
          <button
            className={styles.primaryButton}
            onClick={onScratchAnother}
            data-testid="outcome-cta"
          >
            SCRATCH ANOTHER CARD
          </button>
        )}

        <button
          className={styles.secondaryButton}
          onClick={onClose}
          data-testid="outcome-close"
        >
          Return to Portal
        </button>
      </div>
    </div>
  );
};
